import { useCallback, useEffect, useState } from 'react';
import {
  associateGenericUploadsResponseSchema,
  contactListSchema,
  genericLinkUploadsResponseSchema,
  uploadLinkResponseSchema,
  type Contact,
  type GenericLinkUploadItem,
} from '@iqz-ddds/contracts';
import { ApiClient, Button, Card, Field, FailureNotice, StatusPill } from '@iqz-ddds/components';

export interface RepToolsFrameProps {
  readonly client: ApiClient;
  readonly onFailure: (err: unknown) => { detail: string; reference?: string };
}

/** Split out so the three states read as three early returns, not a nested ternary. */
interface ActivityListProps {
  readonly submissions: readonly GenericLinkUploadItem[] | undefined;
  readonly onDownload: (documentId: string, documentName: string) => void;
  readonly busyDocumentId: string | undefined;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
}

function ActivityList({
  submissions,
  onDownload,
  busyDocumentId,
  selected,
  onToggle,
}: ActivityListProps): React.JSX.Element {
  if (submissions === undefined) return <p className="ddds-empty">Loading.</p>;
  if (submissions.length === 0) return <p className="ddds-empty">Nobody has used your generic upload link yet.</p>;

  return (
    <div className="ddds-submission-table">
      <div className="ddds-submission-table__head" aria-hidden="true">
        <span />
        <span>Name</span>
        <span>Email</span>
        <span>Document</span>
        <span>Date</span>
        <span>Actions</span>
      </div>
      {submissions.map((sub) => (
        <div className="ddds-submission" key={sub.id}>
          <div className="ddds-submission__row">
            <input
              type="checkbox"
              checked={selected.has(sub.id)}
              onChange={() => onToggle(sub.id)}
              aria-label={`Select ${sub.documentName}`}
            />
            <span>{sub.guestName}</span>
            <span className="ddds-submission__email">{sub.guestEmail}</span>
            <span>{sub.documentName}</span>
            <span className="ddds-row__meta">{new Date(sub.createdAt).toLocaleDateString()}</span>
            <Button
              variant="quiet"
              width="auto"
              onClick={() => onDownload(sub.documentId, sub.documentName)}
              disabled={busyDocumentId !== undefined}
            >
              {busyDocumentId === sub.documentId ? 'Downloading' : 'Download'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RepToolsFrame({ client, onFailure }: RepToolsFrameProps): React.JSX.Element {
  const [submissions, setSubmissions] = useState<readonly GenericLinkUploadItem[] | undefined>(undefined);
  const [uploadLink, setUploadLink] = useState<Awaited<ReturnType<typeof uploadLinkResponseSchema.parse>>['link']>(null);
  const [failure, setFailure] = useState<{ detail: string; reference?: string } | undefined>(undefined);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [busyDocumentId, setBusyDocumentId] = useState<string | undefined>(undefined);
  const [contacts, setContacts] = useState<readonly Contact[]>([]);
  const [selectedUploads, setSelectedUploads] = useState<ReadonlySet<string>>(new Set());
  const [targetContactId, setTargetContactId] = useState('');
  const [associating, setAssociating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [uploadsRes, genericLink, directory] = await Promise.all([
        client.get('/api/v1/upload-links/me/uploads', genericLinkUploadsResponseSchema),
        client.get('/api/v1/upload-links/me', uploadLinkResponseSchema),
        client.get('/api/v1/contacts?limit=100', contactListSchema),
      ]);
      setSubmissions(uploadsRes.uploads);
      setUploadLink(genericLink.link);
      setContacts(directory.contacts);
      // Drop selections for rows that are no longer listed, e.g. after they were associated.
      setSelectedUploads((current) => new Set(uploadsRes.uploads.map((u) => u.id).filter((id) => current.has(id))));
      setFailure(undefined);
    } catch (err) {
      setFailure(onFailure(err));
    }
  }, [client, onFailure]);

  const toggleUpload = useCallback((id: string) => {
    setSelectedUploads((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const associateUploads = useCallback(async () => {
    if (selectedUploads.size === 0 || targetContactId === '') return;
    setAssociating(true);
    setFailure(undefined);
    try {
      await client.post(
        '/api/v1/upload-links/me/uploads/associate',
        { uploadIds: [...selectedUploads], contactId: targetContactId },
        associateGenericUploadsResponseSchema,
        { idempotencyKey: `associate:${globalThis.crypto.randomUUID()}` },
      );
      setSelectedUploads(new Set());
      setTargetContactId('');
      await load();
    } catch (err) {
      setFailure(onFailure(err));
    } finally {
      setAssociating(false);
    }
  }, [client, load, onFailure, selectedUploads, targetContactId]);

  const createUploadLink = useCallback(async () => {
    setLinkBusy(true);
    setFailure(undefined);
    try {
      const result = await client.post('/api/v1/upload-links', {}, uploadLinkResponseSchema, {
        idempotencyKey: `upload-link:create:${globalThis.crypto.randomUUID()}`,
      });
      setUploadLink(result.link);
      setLinkCopied(false);
    } catch (err) {
      setFailure(onFailure(err));
    } finally {
      setLinkBusy(false);
    }
  }, [client, onFailure]);

  const revokeUploadLink = useCallback(async () => {
    if (uploadLink === null) return;
    setLinkBusy(true);
    setFailure(undefined);
    try {
      await client.postVoid(`/api/v1/upload-links/${uploadLink.id}/revoke`, {});
      setUploadLink(null);
      setLinkCopied(false);
    } catch (err) {
      setFailure(onFailure(err));
    } finally {
      setLinkBusy(false);
    }
  }, [client, onFailure, uploadLink]);

  const copyUploadLink = useCallback(async () => {
    if (uploadLink === null) return;
    try {
      await globalThis.navigator.clipboard.writeText(uploadLink.url);
      setLinkCopied(true);
    } catch (err) {
      setFailure(onFailure(err));
    }
  }, [onFailure, uploadLink]);

  const downloadDocument = useCallback(
    (documentId: string, documentName: string) => {
      setBusyDocumentId(documentId);
      void (async () => {
        try {
          const content = await client.download(`/api/v1/documents/${documentId}/content`);
          const href = content.kind === 'url' ? content.url : URL.createObjectURL(content.blob);
          const anchor = document.createElement('a');
          anchor.href = href;
          anchor.download = documentName;
          anchor.click();
          if (content.kind === 'blob') URL.revokeObjectURL(href);
        } catch (err) {
          setFailure(onFailure(err));
        } finally {
          setBusyDocumentId(undefined);
        }
      })();
    },
    [client, onFailure],
  );

  useEffect(() => {
    setSubmissions(undefined);
    void load();
  }, [load]);

  return (
    <div className="ddds-page ddds-embedded ddds-rep-tools">
      {failure !== undefined && <FailureNotice detail={failure.detail} reference={failure.reference} />}

      <section className="ddds-rep-tools__panel">
        <h1>My Secure Upload Link</h1>
        <p className="ddds-rep-tools__lede">Share this link with prospects or clients to securely upload documents.</p>
        <Card>
          <div className="ddds-rep-tools__card-heading">
            <div>
              <h2>Your secure upload link</h2>
              <p className="ddds-rep-tools__lede">{uploadLink === null ? 'Create a reusable link for secure uploads.' : 'Your upload link is active. Copy it to share, or revoke it to replace access.'}</p>
            </div>
            {uploadLink !== null && <StatusPill tone="done">Active</StatusPill>}
          </div>
          {uploadLink === null ? (
            <Button onClick={() => void createUploadLink()} disabled={linkBusy}>
              {linkBusy ? 'Creating' : 'Create upload link'}
            </Button>
          ) : (
            <>
              <Field id="ddds-generic-upload-link" label="Your shareable link" value={uploadLink.url} onChange={() => undefined} disabled />
              <div className="ddds-rep-tools__actions">
                <Button width="auto" onClick={() => void copyUploadLink()} disabled={linkBusy}>
                  {linkCopied ? 'Copied' : 'Copy link'}
                </Button>
                <Button width="auto" variant="quiet" onClick={() => void revokeUploadLink()} disabled={linkBusy}>
                  {linkBusy ? 'Revoking' : 'Revoke link'}
                </Button>
              </div>
              <p className="ddds-rep-tools__lede">Anyone with this link can start a secure upload after verifying their contact.</p>
            </>
          )}
        </Card>
      </section>

      <section className="ddds-rep-tools__panel">
        <h1>Upload Link Activity</h1>
        <p className="ddds-rep-tools__lede">View people who have used your upload link and the documents they submitted.</p>
        <Card>
          <h2>Upload Submissions</h2>
          <p className="ddds-rep-tools__lede">People who have used your upload link.</p>
          <ActivityList
            submissions={submissions}
            onDownload={downloadDocument}
            busyDocumentId={busyDocumentId}
            selected={selectedUploads}
            onToggle={toggleUpload}
          />
          {submissions !== undefined && submissions.length > 0 && (
            <div className="ddds-submission-associate">
              <label className="ddds-field" htmlFor="ddds-associate-contact">
                <span className="ddds-field__label">Add selected documents to contact</span>
                <select
                  id="ddds-associate-contact"
                  className="ddds-field__input"
                  value={targetContactId}
                  disabled={associating}
                  onChange={(event) => setTargetContactId(event.target.value)}
                >
                  <option value="">Choose a contact</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.displayName} ({contact.email})
                    </option>
                  ))}
                </select>
              </label>
              <Button
                width="auto"
                onClick={() => void associateUploads()}
                disabled={associating || selectedUploads.size === 0 || targetContactId === ''}
              >
                {associating ? 'Adding' : `Add ${selectedUploads.size === 0 ? '' : `${selectedUploads.size} `}to contact`}
              </Button>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
